import { toSecondPerson, type SecondPerson } from '#shared/organizations/secondPerson'

export interface WelcomeGreetingTemplate {
  withName: string
  withoutName: string
}

export interface WelcomeGreetingPoolOptions {
  locale?: string | null
  hour?: number
  secondPerson?: SecondPerson | string | null
  jurisdiction?: string | null
}

export interface WelcomeGreetingPickOptions extends WelcomeGreetingPoolOptions {
  firstName?: string | null
}

const ES_TU_GREETINGS: readonly WelcomeGreetingTemplate[] = [
  { withName: '¿Cómo estás, {name}?', withoutName: '¿Cómo estás?' },
  { withName: '¿Qué tal, {name}?', withoutName: '¿Qué tal?' },
  { withName: '¿Muchos contratos hoy, {name}?', withoutName: '¿Muchos contratos hoy?' },
  { withName: '¡Regresó {name}! ¿Empezamos?', withoutName: '¡De vuelta! ¿Empezamos?' },
  { withName: '¿Qué quieres revisar hoy, {name}?', withoutName: '¿Qué quieres revisar hoy?' },
  { withName: '¿En qué te ayudo, {name}?', withoutName: '¿En qué te ayudo?' },
]

const ES_VOS_GREETINGS: readonly WelcomeGreetingTemplate[] = [
  { withName: '¿Cómo estás, {name}?', withoutName: '¿Cómo estás?' },
  { withName: '¿Qué tal, {name}?', withoutName: '¿Qué tal?' },
  { withName: '¿Muchos contratos hoy, {name}?', withoutName: '¿Muchos contratos hoy?' },
  { withName: '¡Regresó {name}! ¿Empezamos?', withoutName: '¡De vuelta! ¿Empezamos?' },
  { withName: '¿Qué querés revisar hoy, {name}?', withoutName: '¿Qué querés revisar hoy?' },
  { withName: '¿Qué necesitás ver hoy, {name}?', withoutName: '¿Qué necesitás ver hoy?' },
]

const ES_USTED_GREETINGS: readonly WelcomeGreetingTemplate[] = [
  { withName: '¿Cómo le va, {name}?', withoutName: '¿Cómo le va?' },
  { withName: '¿Cómo está, {name}?', withoutName: '¿Cómo está?' },
  { withName: '¿Qué necesita revisar hoy, {name}?', withoutName: '¿Qué necesita revisar hoy?' },
  { withName: '¿En qué le ayudo hoy, {name}?', withoutName: '¿En qué le ayudo hoy?' },
  { withName: 'Un gusto verle, {name}. ¿Comenzamos?', withoutName: 'Un gusto saludarle. ¿Comenzamos?' },
]

const ES_SV_VOS_GREETINGS: readonly WelcomeGreetingTemplate[] = [
  { withName: '¿Qué ondas, {name}?', withoutName: '¿Qué ondas?' },
  { withName: '¿Todo bien, {name}? ¿Arrancamos?', withoutName: '¿Todo bien? ¿Arrancamos?' },
]

const EN_GREETINGS: readonly WelcomeGreetingTemplate[] = [
  { withName: 'How are things going, {name}?', withoutName: 'How are things going?' },
  { withName: 'Busy contract day, {name}?', withoutName: 'Busy contract day?' },
  { withName: 'Welcome back, {name}! Ready to dive in?', withoutName: 'Welcome back! Ready to dive in?' },
  { withName: 'What are we reviewing today, {name}?', withoutName: 'What are we reviewing today?' },
  { withName: 'How can I help today, {name}?', withoutName: 'How can I help today?' },
]

function isLateNightHour(hour: number): boolean {
  return hour >= 22 || hour < 5
}

function getSpanishTimeGreeting(hour: number): WelcomeGreetingTemplate {
  if (hour < 12) return { withName: 'Buenos días, {name}', withoutName: 'Buenos días' }
  if (hour < 19) return { withName: 'Buenas tardes, {name}', withoutName: 'Buenas tardes' }
  return { withName: 'Buenas noches, {name}', withoutName: 'Buenas noches' }
}

function getEnglishTimeGreeting(hour: number): WelcomeGreetingTemplate {
  if (hour < 12) return { withName: 'Good morning, {name}', withoutName: 'Good morning' }
  if (hour < 19) return { withName: 'Good afternoon, {name}', withoutName: 'Good afternoon' }
  return { withName: 'Good evening, {name}', withoutName: 'Good evening' }
}

/**
 * Build the candidate welcome-greeting pool for the current UI locale, local hour (0–23),
 * Spanish second-person preference (`tu`, `vos`, `usted`), and organization jurisdiction (e.g. `SV`).
 */
export function getWelcomeGreetingPool(options: WelcomeGreetingPoolOptions = {}): WelcomeGreetingTemplate[] {
  const locale = (options.locale ?? 'es').toLowerCase()
  const hour = options.hour ?? new Date().getHours()

  if (locale.startsWith('en')) {
    const pool: WelcomeGreetingTemplate[] = [getEnglishTimeGreeting(hour)]
    if (isLateNightHour(hour)) {
      pool.push({
        withName: 'Late-night contract review, {name}?',
        withoutName: 'Late-night contract review?',
      })
    }
    pool.push(...EN_GREETINGS)
    return pool
  }

  const secondPerson = toSecondPerson(options.secondPerson)
  const jurisdiction = (options.jurisdiction ?? '').trim().toUpperCase()
  const pool: WelcomeGreetingTemplate[] = [getSpanishTimeGreeting(hour)]

  if (secondPerson !== 'usted' && isLateNightHour(hour)) {
    pool.push({
      withName: '¿Todavía revisando contratos, {name}?',
      withoutName: '¿Todavía revisando contratos?',
    })
  }

  if (secondPerson === 'usted') {
    pool.push(...ES_USTED_GREETINGS)
  } else if (secondPerson === 'vos') {
    pool.push(...ES_VOS_GREETINGS)
    if (jurisdiction === 'SV') {
      pool.push(...ES_SV_VOS_GREETINGS)
    }
  } else {
    pool.push(...ES_TU_GREETINGS)
  }

  return pool
}

/**
 * Pick one welcome greeting from the candidate pool using `randomValue` in `[0, 1)`
 * and format it with `firstName` (or the nameless variant when `firstName` is empty).
 */
export function pickWelcomeGreeting(options: WelcomeGreetingPickOptions = {}, randomValue = 0): string {
  const pool = getWelcomeGreetingPool(options)
  const clamped = Number.isFinite(randomValue) ? Math.min(Math.max(randomValue, 0), 0.999999) : 0
  const index = Math.floor(clamped * pool.length)
  const chosen = pool[index] ?? pool[0]!
  const trimmedName = (options.firstName ?? '').trim()

  if (!trimmedName) {
    return chosen.withoutName
  }
  return chosen.withName.replaceAll('{name}', trimmedName)
}
